import React from 'react'
import { useSelector } from 'react-redux'
import { AdminApi } from '../admin.api'
import { IStoreInterface } from '../../configs/store.config'
import { Event as CompanyEvent, LoggedUser } from '../../shared/shared.models'
import { PageTitle } from '../../shared/shared.styled'
import { useAlert } from 'react-alert'
import {
    Table,
    ColumnProps,
} from '../../shared/components/table/table.component'
import ActionButton from '../../shared/components/action-button.component'
import { useNavigate } from 'react-router-dom'
import { RoutesEnum } from '../../routes.constants'
import moment from 'moment'

export const EventsPage = () => {
    const navigate = useNavigate()
    const alert = useAlert()

    const loggedUser = useSelector(
        (state: IStoreInterface) => state.adminReducer.user as LoggedUser
    )

    const [events, setEvents] = React.useState<CompanyEvent[]>([])
    const [isLoading, setIsLoading] = React.useState(true)

    const fetchEvents = React.useCallback(async () => {
        try {
            const res = await AdminApi.methods.getEvents(loggedUser.companyUuid)
            setEvents(res.data.events)
        } catch (e) {
            alert.error('Erro ao buscar os eventos. Por favor tente mais tarde')
        } finally {
            setIsLoading(false)
        }
    }, [alert, loggedUser.companyUuid])

    React.useEffect(() => {
        fetchEvents()
    }, [fetchEvents])

    const navigateToCreateEvent = () => {
        navigate(RoutesEnum.CREATE_EVENT)
    }

    const editEvent = (event: CompanyEvent) => {
        navigate(RoutesEnum.EVENT_PAGE.replace(':eventId', event.uuid))
    }

    const today = moment()

    // "Terminado" wins regardless of the active flag — the daily cron job
    // (app.tasks.expire_events_job) only flips active to false once a day,
    // so an event past its end date should already read as terminated here
    // even on the day it expired but before the cron has run.
    const getStatus = (event: CompanyEvent) => {
        if (moment(event.endDate).isBefore(today, 'day')) return 'terminado'
        if (!event.active) return 'inativo'
        if (moment(event.startDate).isAfter(today, 'day')) return 'agendado'
        return 'ativo'
    }

    const STATUS_LABELS: Record<string, string> = {
        ativo: 'A decorrer',
        terminado: 'Terminado',
        agendado: 'Agendado',
        inativo: 'Inativo',
    }

    // Same light green/red used elsewhere for row status (contacts.page.tsx,
    // sold-vehicles.page.tsx) — scheduled/inactive events stay uncolored.
    const STATUS_ROW_STYLES: Record<string, React.CSSProperties> = {
        ativo: { backgroundColor: '#B3E1A5' },
        terminado: { backgroundColor: '#fcdede' },
    }

    const sortedEvents = [...events].sort((a, b) =>
        moment(b.startDate).diff(moment(a.startDate))
    )

    const columns: ColumnProps[] = [
        {
            key: 'name',
            label: 'Nome',
            ratio: 4 / 10,
            renderFunction: (event) => event.name,
        },
        {
            key: 'startDate',
            label: 'Início',
            ratio: 2 / 10,
            renderFunction: (event) =>
                moment(event.startDate).format('DD/MM/YYYY'),
        },
        {
            key: 'endDate',
            label: 'Fim',
            ratio: 2 / 10,
            renderFunction: (event) =>
                moment(event.endDate).format('DD/MM/YYYY'),
        },
        {
            key: 'status',
            label: 'Estado',
            ratio: 2 / 10,
            renderFunction: (event) => STATUS_LABELS[getStatus(event)],
        },
    ]

    return (
        <>
            <PageTitle>Eventos</PageTitle>
            <Table<CompanyEvent>
                isLoadingData={isLoading}
                rowStyles={(event) => STATUS_ROW_STYLES[getStatus(event)] || {}}
                columns={columns}
                onRowClick={editEvent}
                getUrl={(event) =>
                    RoutesEnum.EVENT_PAGE.replace(':eventId', event.uuid)
                }
                values={sortedEvents}
            />
            <ActionButton onClick={navigateToCreateEvent} />
        </>
    )
}
